/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { DemoCard } from 'ui/molecules/DemoCard/DemoCard';

import styles from './RegistryRecordCard.module.scss';

import type { RegistryRecordCardProps } from './RegistryRecordCard.types';

interface RegistryField {
  label: string;
  value: string;
  pill?: boolean;
}

interface RegistryCompany {
  avatar: string;
  name: string;
  meta: string;
  source: string;
  provenance: string;
  fields: RegistryField[];
}

const COMPANIES: RegistryCompany[] = [
  {
    avatar: 'VS',
    name: 'Verity Systems Group Ltd',
    meta: 'United Kingdom · Private limited company',
    source: 'Companies House',
    provenance: 'Retrieved from Companies House · today',
    fields: [
      { label: 'Registration number', value: '09876543' },
      { label: 'Legal form', value: 'Private limited company' },
      { label: 'Status', value: 'Active', pill: true },
      { label: 'Incorporated', value: '12 Mar 2014' },
      { label: 'Revenue (official)', value: '£48.2M' },
      { label: 'Employees', value: '210' },
      { label: 'GB SIC', value: '62012' },
      { label: 'VAT', value: 'GB 342 118 776' },
    ],
  },
  {
    avatar: 'NL',
    name: 'Nordwind Logistik GmbH',
    meta: 'Germany · Gesellschaft mit beschränkter Haftung',
    source: 'Handelsregister',
    provenance: 'Retrieved from Handelsregister · today',
    fields: [
      { label: 'Registration number', value: 'HRB 118824' },
      { label: 'Legal form', value: 'GmbH' },
      { label: 'Status', value: 'Active', pill: true },
      { label: 'Incorporated', value: '03 Sep 2011' },
      { label: 'Revenue (official)', value: '€72.4M' },
      { label: 'Employees', value: '430' },
      { label: 'WZ 2008', value: '52.29' },
      { label: 'VAT', value: 'DE 815 442 909' },
    ],
  },
];

export const RegistryRecordCard = ({ className }: RegistryRecordCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useIntersection(ref, { continuous: true, threshold: 0.35 });

  const [reduced, setReduced] = useState(false);
  const [companyIdx, setCompanyIdx] = useState(0);
  const [shown, setShown] = useState(0);
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  const playing = inView && !reduced;

  // reduced-motion / out-of-view → jump to a fully resolved first record.
  useEffect(() => {
    if (playing) {
      return;
    }
    setCompanyIdx(0);
    setShown(COMPANIES[0].fields.length);
    setTicked(new Set(COMPANIES[0].fields.map((_, i) => i)));
    setVerified(true);
  }, [playing]);

  useEffect(() => {
    if (!playing) {
      return;
    }

    let cancelled = false;
    const timers: number[] = [];
    const wait = (ms: number) =>
      new Promise<void>((r) => {
        timers.push(window.setTimeout(r, ms));
      });

    const run = async () => {
      let idx = 0;

      while (!cancelled) {
        const company = COMPANIES[idx];

        // reset for the incoming record
        setCompanyIdx(idx);
        setShown(0);
        setTicked(new Set());
        setVerified(false);
        await wait(700);
        if (cancelled) {
          return;
        }

        // reveal each field, then flip its tick shortly after
        for (let i = 0; i < company.fields.length; i += 1) {
          setShown(i + 1);
          await wait(150);
          if (cancelled) {
            return;
          }
          setTicked((s) => new Set(s).add(i));
          await wait(110);
          if (cancelled) {
            return;
          }
        }

        setVerified(true);
        await wait(2200);
        if (cancelled) {
          return;
        }

        idx = (idx + 1) % COMPANIES.length;
      }
    };

    run();

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [playing]);

  const company = COMPANIES[companyIdx];

  const statusNode = verified ? (
    <span className={styles.footVerified}>Verified ✓</span>
  ) : (
    <span>Verifying at source…</span>
  );

  return (
    <div ref={ref} className={cn(styles.wrap, className)}>
      <DemoCard
        title="REGISTRY RECORD"
        status={company.source}
        footerLeft={statusNode}
        footerRight="Every field filterable"
        bodyClassName={styles.body}
      >
        <div className={styles.record}>
          <div className={styles.header}>
            <span className={styles.avatar}>{company.avatar}</span>
            <span className={styles.headText}>
              <span className={styles.name}>{company.name}</span>
              <span className={styles.meta}>{company.meta}</span>
            </span>
          </div>

          <div className={styles.fields}>
            {company.fields.map((field, i) =>
              i < shown ? (
                <div key={field.label} className={styles.field}>
                  <span className={styles.key}>{field.label}</span>
                  <span className={styles.valueWrap}>
                    {field.pill ? (
                      <span className={styles.pill}>{field.value}</span>
                    ) : (
                      <span className={styles.value}>{field.value}</span>
                    )}
                    <span className={cn(styles.tick, ticked.has(i) && styles.tickOn)}>✓</span>
                  </span>
                </div>
              ) : null,
            )}
          </div>

          <div className={styles.provenance}>{company.provenance}</div>
        </div>
      </DemoCard>
    </div>
  );
};
